// packages/chatbot/src/components/ChatWindow.tsx
import { useRef, useEffect, useMemo, useState, CSSProperties, ReactNode } from 'react';
import { Spinner, Alert } from '@dopomogai/ui';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ToolRenderer } from './ToolRenderer';
import type { UIMessage, ToolRegistry, ActiveToolCall, ToolUiConfig } from '../types';

interface ChatWindowProps {
  messages: UIMessage[];
  assistantState: 'idle' | 'thinking' | 'streaming';
  activeToolCall: ActiveToolCall | null;
  onSendMessage: (text: string) => void;
  onSubmitToolResponse: (toolCallId: string, result: any) => void;
  onCancelTool: () => void;
  toolRegistry: ToolRegistry;
  isLoading: boolean;
  error: string | null;
  inputStyle?: CSSProperties;
  userAvatar?: ReactNode | string;
  agentAvatar?: ReactNode | string;
  userMessageStyle?: CSSProperties;
  assistantMessageStyle?: CSSProperties;
  primaryColor?: string;
  initialPrompts?: string[];
  initialMessage?: { content: string };
  streamingMode?: 'realtime' | 'final';
  onReplayFrontendActions?: (toolCallId: string) => void;

  // Optional: provide a stable key to persist drafts/scroll per conversation
  conversationKey?: string;

  // NEW: controls how tool messages render
  toolUi?: ToolUiConfig;
}

const LoadingSkeleton = () => {
  return (
    <div className="space-y-3 animate-pulse" aria-label="Loading conversation">
      <div className="flex gap-3 w-full max-w-[85%] mr-auto justify-start">
        <div className="w-8 h-8 rounded-lg bg-slate-200" />
        <div className="flex-1">
          <div className="w-3/4 h-4 bg-slate-200 rounded mb-2" />
          <div className="w-2/3 h-4 bg-slate-200 rounded" />
        </div>
      </div>
      <div className="flex gap-3 w-full max-w-[85%] ml-auto justify-end">
        <div className="flex-1">
          <div className="w-1/2 h-4 bg-slate-200 rounded mb-2 ml-auto" />
          <div className="w-2/5 h-4 bg-slate-200 rounded ml-auto" />
        </div>
        <div className="w-8 h-8 rounded-lg bg-slate-200" />
      </div>
    </div>
  );
};

export function ChatWindow({
  messages,
  assistantState,
  activeToolCall,
  onSendMessage,
  onSubmitToolResponse,
  onCancelTool,
  toolRegistry,
  isLoading,
  error,
  inputStyle,
  userAvatar,
  agentAvatar,
  userMessageStyle,
  assistantMessageStyle,
  primaryColor,
  initialPrompts,
  streamingMode = 'realtime',
  onReplayFrontendActions,
  conversationKey,
  toolUi,
}: ChatWindowProps) {
  // Compute a fallback key if none provided
  const computedConversationKey = useMemo(() => {
    if (conversationKey && typeof conversationKey === 'string') return conversationKey;
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    return `site:${path}`;
  }, [conversationKey]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [didRestoreScroll, setDidRestoreScroll] = useState(false);

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    });
  };

  // Always jump to bottom on key change
  useEffect(() => {
    setDidRestoreScroll(false);
  }, [computedConversationKey]);

  // First mount/restore then force bottom
  useEffect(() => {
    if (didRestoreScroll) return;
    const el = containerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
      setDidRestoreScroll(true);
    });
  }, [computedConversationKey, didRestoreScroll]);

  // Always scroll to bottom when new content arrives
  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  // Also scroll to bottom when tool UI appears or disappears
  useEffect(() => {
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeToolCall?.callId]);

  const stopPageScroll = (e: React.UIEvent) => e.stopPropagation();

  const showInitialPrompts = !isLoading && messages.length < 2 && !!initialPrompts?.length;

  // Typing indicator (thinking) if no streaming message exists yet
  const showTypingDots = useMemo(() => {
    if (assistantState !== 'thinking') return false;
    const last = messages[messages.length - 1];
    return !(last && last.type === 'assistant' && (last as any).isStreaming);
  }, [assistantState, messages]);

  // Build a resizeKey so MessageInput recalculates its height on layout/content changes
  const resizeKey = `${assistantState}|${activeToolCall?.callId || ''}|${messages.length}`;

  // Disable input while loading or assistant is not idle
  const inputDisabled = isLoading || assistantState !== 'idle';

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto overscroll-contain p-4 lg:p-6 space-y-4 bg-white"
        onWheel={stopPageScroll}
        onTouchMove={stopPageScroll}
      >
        {isLoading && messages.length === 0 && (
          <div className="flex flex-col gap-4">
            <LoadingSkeleton />
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Spinner size="sm" />
              <span>Loading conversation…</span>
            </div>
          </div>
        )}

        {error && <Alert type="error" title="An error occurred" message={error} />}

        <MessageList
          messages={
            streamingMode === 'final'
              ? messages.map((m, idx, arr) => {
                  if (m.type === 'assistant' && (m as any).isStreaming && idx === arr.length - 1) {
                    return { ...m, content: '' };
                  }
                  return m;
                })
              : messages
          }
          userAvatar={userAvatar}
          agentAvatar={agentAvatar}
          userMessageStyle={userMessageStyle}
          assistantMessageStyle={assistantMessageStyle}
          onReplayFrontendActions={onReplayFrontendActions}
          toolUi={toolUi}
        />

        {showTypingDots && (
          <div className="flex gap-3 w-full max-w-[85%] mr-auto justify-start">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-500">
              <span className="w-2 h-2 rounded-full bg-white/90" />
            </div>
            <div className="flex flex-col items-start">
              <div className="border-2 border-black shadow-none mb-0 bg-slate-100 rounded-lg px-3 py-2">
                <span className="typing-dots" aria-label="Assistant is typing">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </span>
              </div>
            </div>
          </div>
        )}

        {activeToolCall && (
          <ToolRenderer
            toolName={activeToolCall.toolName}
            args={activeToolCall.args}
            toolRegistry={toolRegistry}
            onSubmit={(result: any) => onSubmitToolResponse(activeToolCall.callId, result)}
            onCancel={onCancelTool}
          />
        )}
      </div>

      <div className="flex-shrink-0 border-t-2 border-black" style={{ backgroundColor: '#F0F4F8', ...inputStyle }}>
        {showInitialPrompts && (
          <div className="p-4 pt-2 border-b border-slate-300">
            <p className="text-xs text-slate-500 mb-2 font-semibold">Or try one of these:</p>
            <div className="flex flex-wrap gap-2">
              {initialPrompts!.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => onSendMessage(prompt)}
                  disabled={assistantState !== 'idle'}
                  className="px-3 py-1.5 bg-white border-2 border-black rounded-lg text-sm font-medium text-slate-700 transition-all duration-150 transform hover:-translate-y-px hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] active:translate-y-0 active:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="px-4 py-2">
          <MessageInput
            disabled={inputDisabled}
            buttonDisabled={assistantState !== 'idle' || isLoading}
            onSendMessage={onSendMessage}
            primaryColor={primaryColor}
            placeholder={isLoading ? 'Loading conversation…' : 'Type your message...'}
            conversationKey={computedConversationKey}
            resizeKey={resizeKey}
          />
        </div>
      </div>
    </div>
  );
}
